import { createTheme, ThemeProvider } from "@mui/material/styles";
import React from "react";
import ReactDOM from "react-dom/client";
import "./index.css";
import App from "./App";
import { CssBaseline } from "@mui/material";

const theme = createTheme({
  palette: {
    primary: {
      main: "#E50000",
      light: "#FF4D4D",
      dark: "#B80000",
      contrastText: "#FFFFFF",
    },
    secondary: {
      main: "#FF8080",
      light: "#FFB3B3",
      dark: "#CC3333",
      contrastText: "#FFFFFF",
    },
    background: {
      default: "#F7F8FC",
      paper: "#FFFFFF",
    },
    text: {
      primary: "#111827",
      secondary: "#6B7280",
    },
    success: { main: "#10B981", light: "#D1FAE5", dark: "#065F46" },
    error: { main: "#EF4444", light: "#FEE2E2", dark: "#991B1B" },
    info: { main: "#3B82F6", light: "#DBEAFE", dark: "#1E40AF" },
    warning: { main: "#F59E0B", light: "#FEF3C7", dark: "#92400E" },
  },
  typography: {
    fontFamily:
      "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    h4: { fontWeight: 700, letterSpacing: "-0.015em" },
    h5: { fontWeight: 700, letterSpacing: "-0.01em" },
    h6: { fontWeight: 600 },
    subtitle1: { fontWeight: 500 },
    subtitle2: { fontWeight: 600 },
    button: { fontWeight: 600, textTransform: "none", letterSpacing: "0.01em" },
  },
  shape: { borderRadius: 10 },
  components: {
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          boxShadow:
            "0 1px 3px rgba(0,0,0,0.07), 0 1px 2px rgba(0,0,0,0.04)",
          border: "1px solid #F0F0F5",
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 12,
        },
        elevation3: {
          boxShadow:
            "0 1px 3px rgba(0,0,0,0.07), 0 1px 2px rgba(0,0,0,0.04)",
          border: "1px solid #F0F0F5",
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          textTransform: "none",
          fontWeight: 600,
          boxShadow: "none",
          paddingTop: 9,
          paddingBottom: 9,
          "&:hover": { boxShadow: "none" },
        },
        containedPrimary: {
          background: "linear-gradient(135deg, #E50000 0%, #B80000 100%)",
          "&:hover": {
            background: "linear-gradient(135deg, #CC0000 0%, #990000 100%)",
            boxShadow: "0 4px 12px rgba(229,0,0,0.35)",
          },
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          "& .MuiOutlinedInput-root": {
            borderRadius: 8,
            backgroundColor: "#FAFAFA",
            "&:hover .MuiOutlinedInput-notchedOutline": {
              borderColor: "#E50000",
            },
            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
              borderColor: "#E50000",
              borderWidth: 1.5,
            },
          },
          "& .MuiInputLabel-root.Mui-focused": {
            color: "#E50000",
          },
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        colorDefault: {
          backgroundColor: "#FFFFFF",
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: { borderRadius: 6, fontWeight: 500, fontSize: "0.8rem" },
      },
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          "& .MuiTableCell-head": {
            fontWeight: 600,
            fontSize: "0.72rem",
            textTransform: "uppercase",
            letterSpacing: "0.07em",
            color: "#6B7280",
            backgroundColor: "#F9FAFB",
          },
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          "&:last-child td": { borderBottom: 0 },
          "&.MuiTableRow-hover:hover": {
            backgroundColor: "#FFF5F5",
          },
        },
      },
    },
    MuiToggleButton: {
      styleOverrides: {
        root: {
          textTransform: "none",
          fontWeight: 500,
          fontSize: "0.875rem",
          borderRadius: "8px !important",
          "&.Mui-selected": {
            backgroundColor: "#E50000",
            color: "#FFFFFF",
            "&:hover": {
              backgroundColor: "#CC0000",
            },
          },
        },
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: { borderColor: "#F0F0F5" },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
        },
      },
    },
  },
});

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <ThemeProvider theme={theme}>
    <CssBaseline />
    <App />
  </ThemeProvider>
);
